import { isState } from "@snap/state-management";
import type { Observable } from "rxjs";
import { filter, map, scan, Subject, takeUntil } from "rxjs";
import { Injectable } from "@snap/ts-inject";
import { entries } from "../../common/entries";
import { stringifyError, stringifyErrorMessage } from "../../common/errorHelpers";
import { TypedCustomEvent } from "../../events/TypedCustomEvent";
import { logEntriesFactory } from "../../logger/logEntries";
import type { LogEntry } from "../../logger/logger";
import { logLevelMap } from "../../logger/logger";
import type { LensState } from "../../session/lensState";
import type { MetricsEventTarget } from "../metricsEventTarget";
import { metricsEventTargetFactory } from "../metricsEventTarget";
import type { MetricsClient } from "../../clients/metricsClient";
import { metricsClientFactory } from "../../clients/metricsClient";
import { Count } from "../operational/Count";

const logMethods = entries(logLevelMap).map(([level]) => level);

// How many log entries to include as the error context
const maxBufferedEntries = 15;
const contextSeparator = "\n\n----------------- Context -----------------\n\n";
const methodLength = logMethods.reduce((max, method) => Math.max(max, method.length), 0);

interface RepeatableLogEntry {
    entry: LogEntry;
    count: number;
    lastTime: Date;
}

interface EntriesBuffer {
    /**
     * LogEntries grouped by their message.
     */
    entries: RepeatableLogEntry[];
    /**
     * The recent log entry.
     */
    recent: LogEntry;
}

export function getContextString(logEntries: RepeatableLogEntry[]) {
    const result = [];
    for (const { entry, count, lastTime } of logEntries) {
        const time = entry.time.toISOString();
        const method = entry.level.padStart(methodLength);
        const messages = entry.messages.map(prettyPrintMessage).join(" ");
        let dupSuffix =
            count > 1 ? ` (Repeated ${count} times with the last occurrence at ${lastTime.toISOString()})` : "";

        result.push(`${time} [${entry.module}] ${method}: ${messages}${dupSuffix}`);
    }
    return result.join("\n");
}

/**
 * Pretty print a log message.
 */
function prettyPrintMessage(message: unknown): string {
    if (message instanceof Error) return stringifyErrorMessage(message);
    if (message instanceof Date) return message.toISOString();
    return message + "";
}

export function reportExceptionToBlizzard(
    logEntries: Observable<LogEntry>,
    metricsEventTarget: MetricsEventTarget,
    metrics: MetricsClient,
    lensState?: LensState
) {
    logEntries
        .pipe(
            scan<LogEntry, EntriesBuffer>(
                ({ entries }, newEntry) => {
                    const lastEntry = entries[entries.length - 1];
                    const isNewEntryRepeated =
                        lastEntry &&
                        lastEntry.entry.messages.join() === newEntry.messages.join() &&
                        lastEntry.entry.level === newEntry.level;
                    if (isNewEntryRepeated) {
                        lastEntry.count += 1;
                        lastEntry.lastTime = newEntry.time;
                    } else {
                        entries.push({
                            entry: newEntry,
                            count: 1,
                            lastTime: newEntry.time,
                        });
                    }
                    return {
                        entries: entries.slice(-maxBufferedEntries),
                        recent: newEntry,
                    };
                },
                // Start with a dummy recent entry -- it gets overridden each time we handle a log entry.
                { entries: [], recent: { time: new Date(), module: "any", level: "debug", messages: [] } }
            ),
            filter(({ recent }) => recent.level === "error"),
            map(({ entries, recent }) => ({
                context: entries,
                error: recent.messages.find((e) => e instanceof Error) as Error,
            })),
            filter(({ error }) => !!error)
        )
        .subscribe(({ error, context }) => {
            const currentLensState = lensState?.getState();
            const lensId =
                currentLensState && !isState(currentLensState, "noLensApplied") ? currentLensState.data.id : "none";
            metricsEventTarget.dispatchEvent(
                new TypedCustomEvent("exception", {
                    name: "exception",
                    lensId,
                    type: error.name,
                    reason: `${stringifyError(error)}${contextSeparator}${getContextString(context)}`,
                })
            );

            metrics.setOperationalMetrics(Count.count("handled_exception", 1, { type: error.name }));
        });
}

export interface GlobalExceptionReporter {
    attachLensContext: (lensState: LensState) => void;
}

/**
 * Reports log entries to Blizzard when there is no CameraKit session yet.
 *
 * @internal
 */
export const reportGlobalException = Injectable(
    "reportGlobalException",
    [logEntriesFactory.token, metricsEventTargetFactory.token, metricsClientFactory.token] as const,
    (
        logEntries: Observable<LogEntry>,
        metricsEventTarget: MetricsEventTarget,
        metrics: MetricsClient
    ): GlobalExceptionReporter => {
        // Initially we log exceptions without any lens context
        const cancellationSubject = new Subject<void>();
        reportExceptionToBlizzard(logEntries.pipe(takeUntil(cancellationSubject)), metricsEventTarget, metrics);

        // Later session scope reporter triggers cancellation of the global one
        // and initiates exception reporting with a lens context
        return {
            attachLensContext: (lensState: LensState) => {
                cancellationSubject.next();
                reportExceptionToBlizzard(logEntries, metricsEventTarget, metrics, lensState);
            },
        };
    }
);
